import { useEffect, useState, type FormEvent } from "react";
import {
  MUNICIPAL_ADMIN_ASSIGNABLE_ROLES,
  SYSTEM_ADMIN_ASSIGNABLE_ROLES,
  type AccountStatus,
  type AccountSummary,
  type GeographicScope,
  type RoleCode,
} from "@rbim/shared";
import { Trash2, UserPlus, Users, X } from "lucide-react";
import { apiRequest, ApiRequestError } from "../api";
import { useAuth } from "../auth";
import { RbacTestAccountsPanel } from "./rbac-test-accounts-panel";

export const assignableRoles: RoleCode[] = [
  ...MUNICIPAL_ADMIN_ASSIGNABLE_ROLES,
];
export const systemAssignableRoles: RoleCode[] = [
  ...SYSTEM_ADMIN_ASSIGNABLE_ROLES,
];

type MunicipalityOption = {
  id: string;
  name: string;
  type: "MUNICIPALITY" | "CITY";
  psgcCode: string;
  provinceName?: string;
};
type BarangayOption = { id: string; municipalityId: string; name: string; psgcCode: string };
type GeographyResponse = {
  municipality?: MunicipalityOption | null;
  municipalities?: MunicipalityOption[];
  barangays: BarangayOption[];
};

export function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const systemAuthority = currentUser?.role === "SYSTEM_ADMINISTRATOR";
  const roleOptions = systemAuthority ? systemAssignableRoles : assignableRoles;
  const [users, setUsers] = useState<AccountSummary[]>([]);
  const [geography, setGeography] = useState<GeographyResponse | null>(null);
  const [message, setMessage] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const loadUsers = () => apiRequest<{ users: AccountSummary[] }>(systemAuthority ? "/system/users" : "/admin/users")
    .then((response) => setUsers(response.users))
    .catch(showError(setMessage));

  useEffect(() => {
    void loadUsers();
    apiRequest<GeographyResponse>(systemAuthority ? "/system/geography" : "/admin/geography")
      .then(setGeography)
      .catch(showError(setMessage));
  }, [systemAuthority]);

  async function updateStatus(user: AccountSummary, status: AccountStatus) {
    setMessage("");
    const endpoint = systemAuthority ? `/system/users/${user.id}/status` : `/admin/users/${user.id}/status`;
    try { await apiRequest(endpoint, { method: "PATCH", body: JSON.stringify({ status }) }); await loadUsers(); }
    catch (error) { showError(setMessage)(error); }
  }

  async function deleteAccount(user: AccountSummary) {
    if (systemAuthority || user.status !== "DEACTIVATED" || !assignableRoles.includes(user.role)) return;

    const enteredReason = window.prompt(
      `Enter the reason for permanently deleting @${user.username}. This action removes the login account from the central RBIM server.`,
    );
    if (enteredReason === null) return;

    const reason = enteredReason.trim();
    if (reason.length < 3) {
      setMessage("Enter a deletion reason of at least 3 characters.");
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete ${user.displayName} (@${user.username})? Household and resident RBIM records will be preserved, but this account will no longer be able to sign in or synchronize.`,
    );
    if (!confirmed) return;

    setMessage("");
    setDeletingUserId(user.id);
    try {
      await apiRequest(`/admin/users/${user.id}`, {
        method: "DELETE",
        body: JSON.stringify({ reason }),
      });
      setMessage(`Account @${user.username} was permanently deleted. RBIM resident and household records were preserved.`);
      await loadUsers();
    } catch (error) {
      showError(setMessage)(error);
    } finally {
      setDeletingUserId(null);
    }
  }

  const displayedUsers = systemAuthority
    ? users
    : users.filter((item) =>
        item.role !== "SYSTEM_ADMINISTRATOR"
        && item.role !== "TECHNICAL_ADMINISTRATOR"
        && (item.role !== "MUNICIPAL_CITY_ADMINISTRATOR" || item.id === currentUser?.id),
      );

  const manageableUsers = systemAuthority
    ? users.filter((item) => item.role === "MUNICIPAL_CITY_ADMINISTRATOR" || item.role === "TECHNICAL_ADMINISTRATOR")
    : users.filter((item) =>
        item.role !== "SYSTEM_ADMINISTRATOR"
        && item.role !== "TECHNICAL_ADMINISTRATOR"
        && item.role !== "MUNICIPAL_CITY_ADMINISTRATOR",
      );

  return (
    <section className="portal-page user-management-page">
      <header className="page-heading user-management-heading">
        <div>
          <span className="eyebrow">Administration</span>
          <h1>User management</h1>
          <p>{systemAuthority
            ? "Create and manage elevated administrator accounts across approved Region X municipalities and cities."
            : "Manage operational accounts and Barangay assignments within this Municipality/City."}</p>
        </div>
        <button
          type="button"
          className={showCreate ? "secondary-button user-management-create-toggle" : "primary-button user-management-create-toggle"}
          aria-expanded={showCreate}
          aria-controls="create-user-account"
          onClick={() => setShowCreate(!showCreate)}
        >
          {showCreate ? <X /> : <UserPlus />}
          {showCreate ? "Close form" : "Create account"}
        </button>
      </header>

      {message && <div className="form-error" role="alert">{message}</div>}

      {showCreate && geography && (
        <CreateUserForm
          geography={geography}
          roleOptions={roleOptions}
          systemAuthority={systemAuthority}
          onCreated={() => { setShowCreate(false); void loadUsers(); }}
        />
      )}

      {systemAuthority
        && geography
        && currentUser?.scope.type === "MUNICIPALITY_CITY"
        && (
          <RbacTestAccountsPanel
            geography={geography}
            deploymentMunicipalityId={currentUser.scope.municipalityId}
          />
        )}

      <section className="table-card user-accounts-card">
        <div className="table-title">
          <Users />
          <div>
            <h2>{systemAuthority ? "Accounts across managed locations" : "Accounts in this Municipality/City"}</h2>
            <p>{systemAuthority
              ? "Review account roles, assigned locations, and access status. Municipality/City Administrators remain responsible for their operational users."
              : "Deactivate an operational account before permanently deleting it. Administrator accounts remain protected."}</p>
          </div>
        </div>
        <div className="responsive-table user-management-table">
          <table>
            <thead><tr><th>User</th><th>Role</th><th>Assigned location</th><th>Status</th><th>Access action</th></tr></thead>
            <tbody>
              {displayedUsers.map((user) => {
                const systemManagedRole = user.role === "MUNICIPAL_CITY_ADMINISTRATOR" || user.role === "TECHNICAL_ADMINISTRATOR";
                const statusLocked = user.role === "SYSTEM_ADMINISTRATOR"
                  || (systemAuthority ? !systemManagedRole : systemManagedRole);
                const canDelete = !systemAuthority
                  && user.status === "DEACTIVATED"
                  && assignableRoles.includes(user.role);
                return (
                  <tr key={user.id}>
                    <td><strong>{user.displayName}</strong><small>@{user.username}</small></td>
                    <td>{formatCode(user.role)}</td>
                    <td>{user.locationLabel}</td>
                    <td><span className={`badge ${user.status === "ACTIVE" ? "available" : ""}`}>{formatCode(user.status)}</span></td>
                    <td>
                      <div className="user-account-actions">
                        <select
                          className="status-select"
                          aria-label={`Change status for ${user.displayName}`}
                          value={user.status}
                          disabled={statusLocked || deletingUserId === user.id}
                          onChange={(event) => void updateStatus(user, event.target.value as AccountStatus)}
                        >
                          <option value="ACTIVE">Active</option>
                          <option value="SUSPENDED">Suspended</option>
                          <option value="DEACTIVATED">Deactivated</option>
                        </select>
                        {canDelete && (
                          <button
                            type="button"
                            className="secondary-button user-delete-account-button"
                            disabled={deletingUserId === user.id}
                            aria-label={`Permanently delete ${user.displayName}`}
                            onClick={() => void deleteAccount(user)}
                          >
                            <Trash2 />
                            {deletingUserId === user.id ? "Deleting..." : "Delete account"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!displayedUsers.length && <tr><td colSpan={5}>No accounts are available for your administrative scope.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {manageableUsers.length > 0 && geography && (
        <AccessMaintenance
          users={manageableUsers}
          geography={geography}
          roleOptions={roleOptions}
          systemAuthority={systemAuthority}
          onChanged={() => void loadUsers()}
        />
      )}
    </section>
  );
}

function AccessMaintenance({ users, geography, roleOptions, systemAuthority, onChanged }: { users: AccountSummary[]; geography: GeographyResponse; roleOptions: RoleCode[]; systemAuthority: boolean; onChanged(): void }) {
  const municipalities = municipalityOptions(geography);
  const [userId, setUserId] = useState(users[0]?.id ?? "");
  const [role, setRole] = useState<RoleCode>(roleOptions[0] ?? "BARANGAY_ENCODER");
  const [municipalityId, setMunicipalityId] = useState(municipalities[0]?.id ?? "");
  const [barangayId, setBarangayId] = useState("");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const selected = users.find((user) => user.id === userId) ?? users[0];
    if (!selected) return;
    setRole(roleOptions.includes(selected.role) ? selected.role : roleOptions[0] ?? "BARANGAY_ENCODER");
    const selectedMunicipalityId = selected.scope.type === "MUNICIPALITY_CITY"
      ? selected.scope.municipalityId
      : municipalityForBarangay(geography, selected.scope.type === "BARANGAY" ? selected.scope.barangayId : "")?.id ?? municipalities[0]?.id ?? "";
    setMunicipalityId(selectedMunicipalityId);
    const firstBarangay = geography.barangays.find((item) => item.municipalityId === selectedMunicipalityId);
    setBarangayId(selected.scope.type === "BARANGAY" ? selected.scope.barangayId : firstBarangay?.id ?? "");
  }, [userId, users, geography, roleOptions]);

  function changeMunicipality(value: string) {
    setMunicipalityId(value);
    setBarangayId(geography.barangays.find((item) => item.municipalityId === value)?.id ?? "");
  }

  async function saveAssignment(event: FormEvent) {
    event.preventDefault(); setMessage("");
    const scope = buildScope(role, municipalityId, barangayId);
    if (!scope) { setMessage("Select an approved location for this role."); return; }
    const endpoint = systemAuthority ? `/system/users/${userId}/assignment` : `/admin/users/${userId}/assignment`;
    try { await apiRequest(endpoint, { method: "PUT", body: JSON.stringify({ role, scope }) }); setMessage("Role and geographic assignment updated; existing sessions were revoked."); onChanged(); }
    catch (error) { showError(setMessage)(error); }
  }

  async function resetAccess(event: FormEvent) {
    event.preventDefault(); setMessage("");
    const endpoint = systemAuthority ? `/system/users/${userId}/reset-access` : `/admin/users/${userId}/reset-access`;
    try { await apiRequest(endpoint, { method: "POST", body: JSON.stringify({ temporaryPassword }) }); setTemporaryPassword(""); setMessage("Account access reset, activated, and existing sessions revoked."); onChanged(); }
    catch (error) { showError(setMessage)(error); }
  }

  return (
    <section className="maintenance-grid user-maintenance-grid">
      <form className="form-card user-maintenance-card" onSubmit={saveAssignment}>
        <div className="section-heading">
          <h2>Role and assigned location</h2>
          <p>{systemAuthority
            ? "Assign a Municipality/City Administrator or Technical Administrator to an approved Municipality/City."
            : "Assign operational roles only within this Municipality/City."}</p>
        </div>
        <label>Account<select value={userId} onChange={(event) => setUserId(event.target.value)}>{users.map((user) => <option value={user.id} key={user.id}>{user.displayName} (@{user.username})</option>)}</select></label>
        <label>Role<select value={role} onChange={(event) => setRole(event.target.value as RoleCode)}>{roleOptions.map((item) => <option key={item} value={item}>{formatCode(item)}</option>)}</select></label>
        <LocationFields role={role} geography={geography} municipalityId={municipalityId} barangayId={barangayId} systemAuthority={systemAuthority} onMunicipalityChange={changeMunicipality} onBarangayChange={setBarangayId} />
        <button className="primary-button user-maintenance-action">Save assignment</button>
      </form>

      <form className="form-card user-maintenance-card" onSubmit={resetAccess}>
        <div className="section-heading"><h2>Reset account access</h2><p>Issue a new temporary password and revoke prior sessions.</p></div>
        <label>Account<select value={userId} onChange={(event) => setUserId(event.target.value)}>{users.map((user) => <option value={user.id} key={user.id}>{user.displayName} (@{user.username})</option>)}</select></label>
        <label>Temporary password<input type="password" autoComplete="new-password" minLength={12} required value={temporaryPassword} onChange={(event) => setTemporaryPassword(event.target.value)} /></label>
        <button className="primary-button user-maintenance-action">Reset access</button>
      </form>

      {message && <div className="form-error maintenance-message">{message}</div>}
    </section>
  );
}

function CreateUserForm({ geography, roleOptions, systemAuthority, onCreated }: { geography: GeographyResponse; roleOptions: RoleCode[]; systemAuthority: boolean; onCreated(): void }) {
  const municipalities = municipalityOptions(geography);
  const initialMunicipalityId = systemAuthority ? "" : municipalities[0]?.id ?? "";
  const [role, setRole] = useState<RoleCode>(roleOptions.includes("BARANGAY_WEB_PORTAL") ? "BARANGAY_WEB_PORTAL" : roleOptions[0] ?? "BARANGAY_ENCODER");
  const [municipalityId, setMunicipalityId] = useState(initialMunicipalityId);
  const [barangayId, setBarangayId] = useState(geography.barangays.find((item) => item.municipalityId === initialMunicipalityId)?.id ?? "");
  const [message, setMessage] = useState("");

  function changeMunicipality(value: string) {
    setMunicipalityId(value);
    setBarangayId(geography.barangays.find((item) => item.municipalityId === value)?.id ?? "");
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setMessage("");
    const form = new FormData(event.currentTarget);
    const scope = buildScope(role, municipalityId, barangayId);
    if (!scope) { setMessage("Select an approved location for this role."); return; }
    try {
      await apiRequest(systemAuthority ? "/system/users" : "/admin/users", { method: "POST", body: JSON.stringify({ username: form.get("username"), displayName: form.get("displayName"), password: form.get("password"), status: "ACTIVE", role, scope }) });
      onCreated();
    } catch (error) { showError(setMessage)(error); }
  }

  return (
    <form id="create-user-account" className="form-card user-create-card" onSubmit={submit}>
      <div className="user-create-card-heading">
        <span className="user-create-card-icon"><UserPlus /></span>
        <div>
          <span className="eyebrow">New account</span>
          <h2>Create an account</h2>
          <p>{systemAuthority
            ? "Create an elevated administrator account and choose the Municipality/City it will manage."
            : "Create an operational account for this Municipality/City or one of its Barangays."}</p>
        </div>
      </div>

      <fieldset className="user-form-section">
        <legend>Account details</legend>
        <div className="user-account-details-grid">
          <label>Display name<input name="displayName" required minLength={2} autoComplete="name" /></label>
          <label>Username<input name="username" required minLength={3} pattern="[a-zA-Z0-9._-]+" autoComplete="off" /></label>
          <label>Temporary password<input name="password" type="password" autoComplete="new-password" required minLength={12} /></label>
        </div>
      </fieldset>

      <fieldset className="user-form-section user-assignment-section">
        <legend>Role and location</legend>
        <div className="user-account-assignment-grid">
          <label>Role<select value={role} onChange={(event) => setRole(event.target.value as RoleCode)}>{roleOptions.map((item) => <option key={item} value={item}>{formatCode(item)}</option>)}</select></label>
          <LocationFields role={role} geography={geography} municipalityId={municipalityId} barangayId={barangayId} systemAuthority={systemAuthority} onMunicipalityChange={changeMunicipality} onBarangayChange={setBarangayId} />
        </div>
        {systemAuthority && <p className="user-location-help">Choose the Municipality/City deliberately. The account will be restricted to the location selected here.</p>}
      </fieldset>

      {message && <div className="form-error">{message}</div>}
      <div className="user-form-actions"><button className="primary-button"><UserPlus />Create account</button></div>
    </form>
  );
}

function LocationFields({ role, geography, municipalityId, barangayId, systemAuthority, onMunicipalityChange, onBarangayChange }: {
  role: RoleCode;
  geography: GeographyResponse;
  municipalityId: string;
  barangayId: string;
  systemAuthority: boolean;
  onMunicipalityChange(value: string): void;
  onBarangayChange(value: string): void;
}) {
  const municipalities = municipalityOptions(geography);
  const isBarangayRole = role === "BARANGAY_ENCODER" || role === "BARANGAY_WEB_PORTAL" || role === "BARANGAY_SUPERVISOR";
  const barangays = geography.barangays.filter((item) => item.municipalityId === municipalityId);
  return <>
    <label>Assigned Municipality / City
      <select value={municipalityId} disabled={!systemAuthority || municipalities.length <= 1} onChange={(event) => onMunicipalityChange(event.target.value)} required>
        {systemAuthority && <option value="" disabled>Select a Municipality / City</option>}
        {municipalities.map((item) => <option key={item.id} value={item.id}>{item.name}{item.provinceName ? ` — ${item.provinceName}` : ""}</option>)}
      </select>
    </label>
    {isBarangayRole && <label>Barangay<select value={barangayId} onChange={(event) => onBarangayChange(event.target.value)} required>{barangays.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>}
  </>;
}

function municipalityOptions(geography: GeographyResponse) {
  if (geography.municipalities?.length) return geography.municipalities;
  return geography.municipality ? [geography.municipality] : [];
}

function municipalityForBarangay(geography: GeographyResponse, barangayId: string) {
  const barangay = geography.barangays.find((item) => item.id === barangayId);
  return municipalityOptions(geography).find((item) => item.id === barangay?.municipalityId);
}

function buildScope(role: RoleCode, municipalityId: string, barangayId: string): GeographicScope | null {
  if (role === "BARANGAY_ENCODER" || role === "BARANGAY_WEB_PORTAL" || role === "BARANGAY_SUPERVISOR") return barangayId ? { type: "BARANGAY", barangayId } : null;
  return municipalityId ? { type: "MUNICIPALITY_CITY", municipalityId } : null;
}

function formatCode(value: string) {
  if (value === "MUNICIPAL_CITY_ADMINISTRATOR") return "Municipality/City Administrator";
  if (value === "MUNICIPAL_CITY_USER") return "Municipality/City User";
  if (value === "BARANGAY_WEB_PORTAL") return "Barangay Web Portal";
  return value.split("_").map((part) => part[0] + part.slice(1).toLowerCase()).join(" ");
}
function showError(setter: (message: string) => void) { return (error: unknown) => setter(error instanceof ApiRequestError ? error.message : "The request could not be completed."); }
